import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const tfwaCannes: ChauffeurPage = {
  slug: "tfwa-cannes",
  title: "Private chauffeur TFWA Cannes – business VTC",
  subtitle:
    "Private chauffeur during the TFWA World Exhibition in Cannes: executive transfers, multi-stop meetings around the Palais and brand evenings.",
  keywords: [
    "tfwa cannes chauffeur",
    "tfwa cannes private chauffeur",
    "travel retail chauffeur cannes",
  ],
  hero: {
    title: "Private chauffeur during the TFWA World Exhibition",
    subtitle:
      "In early October, the world's largest travel-retail / duty-free trade show takes over the Palais: a business chauffeur for back-to-back meeting days.",
    keywords: [
      "TFWA Cannes chauffeur",
      "travel retail VTC Cannes",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the TFWA World Exhibition in Cannes",
    badges: ["Executive transfers", "Multi-stop meetings", "Brand evenings"],
  },
  benefits: {
    heading: "Why a chauffeur during the TFWA",
    items: [
      {
        title: "Dense B2B schedule",
        description:
          "The show chains meetings and negotiations all day: a chauffeur waiting between appointments absorbs the inevitable schedule slips.",
      },
      {
        title: "Multi-stop brands & hotels",
        description:
          "Stands at the Palais, breakfasts at the Carlton, lunches at the Martinez: we link your points with no wasted time or parking.",
      },
      {
        title: "Nice airport for executives",
        description:
          "Arrivals and departures of international teams: flight tracking, welcome and a direct transfer to your Cannes hotel.",
      },
      {
        title: "Business dinners & evenings",
        description:
          "Brand cocktails, client dinners and Croisette evenings: late return with a chauffeur who waits.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during TFWA Cannes",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Cannes link with flight tracking for the arrivals and departures of travel-retail delegations.",
      },
      {
        title: "Executive hourly hire",
        text: "A dedicated chauffeur by the half-day or day to chain meetings at the Palais and in town.",
      },
      {
        title: "Multi-stop circuits",
        text: "Optimised routes between stands, hotels and restaurants to keep a tight meeting schedule on track.",
      },
      {
        title: "Team & roadshow shuttles",
        text: "Multi-vehicle coordination for brand delegations, partners and commercial roadshows.",
      },
      {
        title: "Brand dinners & cocktails",
        text: "Receptions, client dinners and business evenings, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during TFWA",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "The show's exhibition halls, brand stands and conference spaces.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic: hospitality suites, breakfasts and business dinners.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, frequent executive round-trips in early October.",
      },
      {
        name: "Mougins & hinterland",
        description: "Client dinners and gourmet tables outside the centre.",
      },
      {
        name: "Mandelieu / Cannes-la-Bocca",
        description: "Additional hotels and team logistics.",
      },
    ],
  },
  generic: {
    heading: "The TFWA World Exhibition from a chauffeur's seat",
    subheading:
      "In early October, global travel retail gathers at the Palais — a B2B show where every meeting slot counts.",
    paragraphs: [
      "Every early October, the TFWA World Exhibition & Congress brings the global duty-free and travel-retail industry to Cannes. At the Palais des Festivals, the days are a succession of meetings, negotiations and brand presentations. A chauffeur waiting between appointments saves the minutes that make the difference on a dense B2B schedule.",
      "Delegations arrive and depart via Nice Côte d'Azur airport over a few days. Flight tracking, welcome and a direct transfer to the hotel absorb the peaks and start the show with no waiting.",
      "The show doesn't stop at the halls: breakfasts at the Carlton, lunches at the Martinez, brand cocktails and client dinners punctuate the Croisette. Multi-stop circuits and hourly hire keep the same chauffeur, with late-night returns after the business evenings.",
    ],
    bullets: [
      "Multi-stop meetings",
      "Flight tracking in early October",
      "Returns after the cocktails",
    ],
  },
  process: {
    heading: "Booking a chauffeur for TFWA Cannes",
    steps: [
      "Tell us your dates, your hotel and your meeting schedule at the Palais and in town.",
      "We build the multi-stop circuits and airport links around your agenda.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Daily and evening hire for meetings, lunches and brand cocktails.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur during TFWA Cannes?",
      a: "Book ahead: in early October, the show fills Cannes and hotels sell out. Share your dates, your hotel and your meeting schedule so we can secure a dedicated chauffeur for the whole of TFWA.",
    },
    {
      q: "Can you handle a multi-stop meeting schedule?",
      a: "Yes. We build optimised circuits between the Palais, the palaces' hospitality suites and the restaurants, with a chauffeur waiting between appointments to absorb timing shifts.",
    },
    {
      q: "Do you handle airport transfers for international teams?",
      a: "Yes, with flight tracking and welcome. We coordinate the arrivals and departures of travel-retail delegations between Nice airport and your Cannes hotel, including multi-vehicle shuttles.",
    },
    {
      q: "Do you cover client dinners and brand cocktails in the evening?",
      a: "Yes. Business evenings, client dinners and brand cocktails end late on the Croisette or in the hinterland: the chauffeur waits and guarantees your late-night return.",
    },
  ],
  relatedLinks: [
    {
      label: "Cannes ⇄ Nice Airport, rate and duration",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Cannes ⇄ Monaco, rate and duration",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Book online",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for TFWA Cannes",
    text: "Executive transfers, multi-stop circuits and brand evenings: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title:
      "TFWA Cannes Chauffeur — Business Travel-Retail car service | KS Rent Services",
    description:
      "Private chauffeur during the TFWA World Exhibition in Cannes: executive transfers, multi-stop circuits around the Palais, Nice airport runs and brand evenings.",
    canonical: "/en/chauffeur/tfwa-cannes",
  },
};

export default tfwaCannes;
